import React, { useState, useEffect, useRef } from 'react';
import { bool, func, number, object } from 'prop-types';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, FieldTextInput } from '../../../components';
import Icons from '../../Icons/Icons';
import IconEyeClose from '../../IconEyeClose/IconEyeClose';
import IconEyeOpen from '../../IconEyeOpen/IconEyeOpen';

import css from './SignInDrawer.module.css';
import LogoImage from '../../../assets/logo.png';
import BannerImage1 from '../../../assets/loginBanner1.png';
import BannerImage2 from '../../../assets/loginBanner2.png';

const SignInDrawer = props => {
  const {
    isOpen,
    onClose,
    intl,
    onSubmitLogin,
    loginInProgress,
    loginError,
    onClearLoginError,
    defaultToLogin,
    fullScreenOnMobile,
    modeVersion,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const formRef = useRef(null);
  const previousLoginInProgress = useRef(false);
  const previousIsOpen = useRef(false);
  const previousLocation = useRef(location.pathname);

  useEffect(() => {
    if (isOpen && !previousIsOpen.current) {
      setShowLoginForm(Boolean(defaultToLogin));
      setShowPassword(false);
      if (defaultToLogin && onClearLoginError) {
        onClearLoginError();
      }
    }

    if (!isOpen && previousIsOpen.current) {
      setShowLoginForm(false);
      setShowPassword(false);
      if (formRef.current) {
        formRef.current.reset();
      }
      if (onClearLoginError) {
        onClearLoginError();
      }
    }

    previousIsOpen.current = isOpen;
  }, [isOpen, defaultToLogin, onClearLoginError]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setShowLoginForm(Boolean(defaultToLogin));
    setShowPassword(false);
    if (defaultToLogin && onClearLoginError) {
      onClearLoginError();
    }
  }, [defaultToLogin, isOpen, onClearLoginError, modeVersion]);

  useEffect(() => {
    if (previousLoginInProgress.current && !loginInProgress && !loginError && showLoginForm) {
      onClose();
      setShowLoginForm(false);
    }
    previousLoginInProgress.current = loginInProgress;
  }, [loginInProgress, loginError, showLoginForm, onClose]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }

    const body = document.body;
    const html = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyTouchAction = body.style.touchAction;
    const previousHtmlOverflow = html.style.overflow;

    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    html.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousBodyOverflow;
      body.style.touchAction = previousBodyTouchAction;
      html.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  // Close drawer when route changes
  useEffect(() => {
    if (isOpen && location.pathname !== previousLocation.current) {
      onClose();
    }
    previousLocation.current = location.pathname;
  }, [location.pathname, isOpen, onClose]);

  const handleClose = () => {
    setShowLoginForm(false);
    if (onClearLoginError) {
      onClearLoginError();
    }
    onClose();
  };

  const handleLogIn = () => {
    setShowLoginForm(true);
  };

  const handleLoginSubmit = values => {
    if (onSubmitLogin) {
      onSubmitLogin({ email: values.email, password: values.password }).catch(() => {});
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={classNames(css.overlay, fullScreenOnMobile ? css.fullScreenMobile : null)}
      data-testid="sign-in-drawer-overlay"
    >
      <div className={css.drawer}>
        <img
          src={BannerImage1}
          alt={intl.formatMessage({ id: 'SignInDrawer.bannerLeftAlt' })}
          className={css.banner1}
        />
        <img
          src={BannerImage2}
          alt={intl.formatMessage({ id: 'SignInDrawer.bannerRightAlt' })}
          className={css.banner2}
        />

        <div className={css.header}>
          <button
            className={css.closeButton}
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: 'Drawer.closeButton' })}
          >
            <Icons name="signInClose" />
          </button>
        </div>

        {!showLoginForm ? (
          <div className={css.content}>
            <div className={css.mainSection}>
              <div className={css.logoSection}>
                <img
                  src={LogoImage}
                  alt={intl.formatMessage({ id: 'SignInDrawer.logoAlt' })}
                  className={css.logo}
                />
              </div>

              <div className={css.welcomeSection}>
                <h3 className={css.welcomeText}>
                  <FormattedMessage id="SignInDrawer.welcome" />
                </h3>
              </div>

              <div className={css.buttonsSection}>
                <button className={css.actionButton} onClick={handleLogIn}>
                  <FormattedMessage id="SignInDrawer.logIn" />
                </button>
              </div>
            </div>

          </div>
        ) : (
          <div className={css.loginContent}>
            <FinalForm
              onSubmit={handleLoginSubmit}
              render={({ handleSubmit, invalid, form }) => {
                // Store form reference for reset
                if (formRef.current !== form) {
                  formRef.current = form;
                }
                const emailPlaceholder = intl.formatMessage({
                  id: 'LoginForm.emailPlaceholder',
                });
                const emailRequiredMessage = intl.formatMessage({
                  id: 'LoginForm.emailRequired',
                });
                const emailRequired = validators.required(emailRequiredMessage);
                const emailInvalidMessage = intl.formatMessage({
                  id: 'LoginForm.emailInvalid',
                });
                const emailValid = validators.emailFormatValid(emailInvalidMessage);

                const passwordPlaceholder = intl.formatMessage({
                  id: 'LoginForm.passwordPlaceholder',
                });
                const passwordRequiredMessage = intl.formatMessage({
                  id: 'LoginForm.passwordRequired',
                });
                const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);

                const submitDisabled = invalid || loginInProgress;

                return (
                  <Form className={css.loginFormContainer} onSubmit={handleSubmit}>
                    <div className={css.loginFormContent}>
                      <div className={css.loginFormFiled}>
                        <h3 className={css.loginHeading}>
                          <FormattedMessage id="SignInDrawer.loginHeading" />
                        </h3>

                        {loginError && (
                          <div className={css.loginError}>
                            <FormattedMessage id="AuthenticationPage.loginFailed" />
                          </div>
                        )}

                        <div className={css.formFields}>
                          <div className={css.formField}>
                            <p className={css.fieldLabel}>
                              <FormattedMessage id="LoginForm.emailLabel" />
                            </p>
                            <div className={css.inputWrapper}>
                              <FieldTextInput
                                type="email"
                                id="email"
                                name="email"
                                autoComplete="email"
                                placeholder={emailPlaceholder}
                                validate={validators.composeValidators(emailRequired, emailValid)}
                              />
                            </div>
                          </div>

                          <div className={css.formField}>
                            <p className={css.fieldLabel}>
                              <FormattedMessage id="LoginForm.passwordLabel" />
                            </p>
                            <div className={css.passwordFieldWrapper}>
                              <div className={css.inputWrapper}>
                                <FieldTextInput
                                  type={showPassword ? 'text' : 'password'}
                                  id="password"
                                  name="password"
                                  autoComplete="current-password"
                                  placeholder={passwordPlaceholder}
                                  validate={passwordRequired}
                                />
                              </div>
                              <span
                                className={css.passwordToggle}
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <IconEyeClose /> : <IconEyeOpen />}
                              </span>
                            </div>
                          </div>
                        </div>

                        <button
                          type="submit"
                          className={css.loginSubmitButton}
                          disabled={submitDisabled}
                        >
                          <FormattedMessage id="SignInDrawer.loginHeading" />
                        </button>
                      </div>

                      <div className={css.forgotPasswordSection}>
                        <FormattedMessage
                          id="SignInDrawer.forgotPassword"
                          values={{
                            forgotPassLink: (
                              <span
                                className={css.couponLink}
                                onClick={() => {
                                  onClose();
                                  history.push(
                                    '/recover-password'
                                  );
                                }}
                              >
                                <FormattedMessage id="SignInDrawer.couponLink" />
                              </span>
                            ),
                          }}
                        />
                      </div>
                    </div>
                  </Form>
                );
              }}
            />
          </div>
        )}

        <div className={css.decorativeElements}>
          <div className={css.waveTopLeft}></div>
          <div className={css.waveBottomRight}></div>
          <div className={css.raindrops}></div>
        </div>
      </div>
    </div>
  );
};

SignInDrawer.defaultProps = {
  isOpen: false,
  onSubmitLogin: null,
  loginInProgress: false,
  loginError: null,
  onClearLoginError: null,
  defaultToLogin: false,
  fullScreenOnMobile: false,
  modeVersion: 0,
};

SignInDrawer.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  intl: intlShape.isRequired,
  onSubmitLogin: func,
  loginInProgress: bool,
  loginError: object,
  onClearLoginError: func,
  defaultToLogin: bool,
  fullScreenOnMobile: bool,
  // Optional: used to force mode resync when context wants to switch signup/login
  modeVersion: number,
};

export default SignInDrawer;
